import { useCallback } from "react";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError, AxiosResponse } from "axios";
import { NotificationToast } from "Components/Generic/Toast/NotificationToast";
import { useAppState } from "Context/StateContext";
import { Fleet } from "Models/fleet";
import { Filters, Searches, Sorts } from "Models/shipeezi.table";
import { Subcontractor } from "Models/subcontractor";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import ApplicationsService from "Services/applications.service";
import AvailabilityService from "Services/availability.service";
import BranchService from "Services/branch.service";
import FleetService from "Services/fleet.service";
import FleetTypeService from "Services/fleet.type.service";
import JobService from "Services/job.service";
import JobTypeService from "Services/job.type.service";
import MembersService from "Services/members.service";
import RoleService from "Services/role.service";
import StatusesService from "Services/statuses.service";
import SubcontractorService from "Services/subcontractor.service";
import TasksTypesService from "Services/task.type.service";
import TeamService from "Services/teams.service";
import WarehouseService from "Services/warehouse.service";
import { CACHE_TIME, STALE_TIME } from "Util/constants";
import { EquipmentOwnership, FleetTypeMode, Status, SubStatusUsedBy } from "Util/enums";
import getRelatedFilters from "Util/getRelatedFilters";
import {
  ApplicationKeys,
  AvailabilityKeys,
  BranchKeys,
  FleetKeys,
  FleetTypeKeys,
  MemberKeys,
  RoleKeys,
  SubcontractorKeys,
  TaskTypeKeys,
  TeamKeys,
  WarehouseKeys,
} from "Util/queries";

import { PRIVATE_ROUTES } from "../../../../../../../constants";
import useRoutePlannerAction from "./useRoutePlannerActions";

interface UpdateFleet {
  id: number;
  fleet: Fleet;
}

interface CreateFleet {
  fleet: Fleet;
  addToRoutePlanner: boolean;
}

interface AvailabilityCountParams {
  searches?: Searches;
  filters?: Filters;
  equipmentId?: number;
}

interface AvailabilityFiltersParams {
  page?: number;
  pageSize?: number;
  searches?: Searches;
  filters?: Filters;
  equipmentId?: number;
}

interface GetTeamsFiltersParams {
  page?: number;
  pageSize?: number;
  searches?: Searches;
  filters?: Filters;
  equipmentId?: number;
  sorting?: Sorts;
}

interface GetTeamsCountParams {
  equipmentId?: number;
  searches?: Searches;
  filters?: Filters;
  sorting?: Sorts;
}

const useEquipmentActions = () => {
  const { currentUser } = useAppState();
  const { t: translate } = useTranslation();
  const history = useHistory();
  const queryClient = useQueryClient();

  const { useAddToRoutePlanner, useUpdateToRoutePlanner } = useRoutePlannerAction();

  const { mutate: addToRoutePlanner } = useAddToRoutePlanner();
  const { mutate: updateInRoutePlanner } = useUpdateToRoutePlanner();

  const getAllFleet = (
    page?: number,
    pageSize?: number,
    searches?: Searches,
    filters?: Filters,
    sorting?: Sorts
  ) => {
    const includes = [
      { association: "branches" },
      { association: "fleetType" },
      { association: "warehouse", attributes: ["externalUuid"] },
      { association: "endWarehouse", attributes: ["externalUuid"] },
    ];

    return useQuery({
      queryKey: FleetKeys.getAll([
        currentUser?.companyId,
        page,
        pageSize,
        filters,
        searches,
        sorting,
        includes,
      ]),
      queryFn: async () => {
        const response = await FleetService.getAll({
          page,
          pageSize,
          companyId: currentUser?.companyId,
          sortBy: sorting
            ? JSON.stringify([...sorting, ["updatedAt", "desc"]])
            : JSON.stringify([["updatedAt", "desc"]]),
          searchByOr: searches && searches.length > 0 ? JSON.stringify(searches) : "",
          include: JSON.stringify(includes),
          ...filters,
        });

        if (response && response.data) return response.data;

        return [];
      },
      enabled: !!currentUser?.id,
      refetchOnWindowFocus: false,
    });
  };

  const countFleet = (filters?: Filters, searches?: Searches) => {
    return useQuery({
      queryKey: FleetKeys.count([currentUser?.companyId, filters, searches]),
      queryFn: async () => {
        const response = await FleetService.count({
          companyId: currentUser?.companyId,
          searchByOr: searches && searches.length > 0 ? JSON.stringify(searches) : "",
          ...filters,
        });

        if (response && response.data) return response.data;

        return 0;
      },
      enabled: !!currentUser?.id,
      refetchOnWindowFocus: false,
    });
  };

  const useCreateFleet = (callbackSuccess?: () => void) => {
    return useMutation({
      mutationKey: FleetKeys.create(),
      mutationFn: (variables: CreateFleet) =>
        FleetService.create({
          ...variables.fleet,
          capacityM3: Number(variables.fleet?.capacityM3 ?? 0),
          year: Number(variables.fleet?.year ?? 0),
          companyId: currentUser?.companyId!,
        }),
      onSuccess: (response: AxiosResponse<Fleet>, variables: CreateFleet) => {
        NotificationToast.success(translate("operationPerformedSuccessfully"));

        if (currentUser?.company.useRoutePlanner) {
          const { addToRoutePlanner: toAdd, fleet } = variables;

          if (toAdd) addToRoutePlanner({ ...fleet, id: response?.data?.id });
        }

        if (callbackSuccess) callbackSuccess();
      },
      onError: (error: AxiosError<Error>) => {
        if (error && error.response) NotificationToast.error(error?.response?.data?.message);
      },
    });
  };

  const useUpdateFleet = (callbackSuccess?: () => void) => {
    return useMutation({
      mutationKey: FleetKeys.update(),
      mutationFn: ({ id, fleet }: UpdateFleet) =>
        FleetService.update(id, {
          ...fleet,
          capacityM3: Number(fleet?.capacityM3 ?? 0),
          year: Number(fleet?.year ?? 0),
        }),
      onSuccess: (_: AxiosResponse<boolean>, variables: UpdateFleet) => {
        NotificationToast.success(translate("operationPerformedSuccessfully"), {
          id: "request.notification.success.fleet",
        });

        if (currentUser?.company.useRoutePlanner && variables?.fleet.externalUuid) {
          updateInRoutePlanner({ id: variables?.fleet.externalUuid, fleet: variables.fleet });
        }

        if (callbackSuccess) callbackSuccess();
      },
      onError: (error: AxiosError<Error>) => {
        if (error && error.response) NotificationToast.error(error?.response?.data?.message);
      },
    });
  };

  const getAllBranches = () => {
    const branchFilters = {
      companyId: currentUser?.companyId,
      status: Status.ACTIVE,
      sortBy: JSON.stringify([["name", "ASC"]]),
      include: JSON.stringify([{ association: "company" }]),
    };

    return useQuery({
      queryKey: BranchKeys.getAll([...Object.values(branchFilters)]),
      queryFn: async () => {
        const response = await BranchService.getAll(branchFilters);

        if (response?.data) return response.data;

        return [];
      },
      enabled: !!currentUser?.companyId,

      initialData() {
        return [];
      },
    });
  };

  const getAllWarehouses = () => {
    const warehouseFilters = {
      status: Status.ACTIVE,
      companyId: currentUser?.companyId,
      sortBy: JSON.stringify([["name", "ASC"]]),
    };

    return useQuery({
      queryFn: async () => WarehouseService.getAll(warehouseFilters).then(({ data }) => data),
      queryKey: WarehouseKeys.getAll([...Object.values(warehouseFilters)]),
      enabled: !!currentUser?.companyId,
      refetchOnWindowFocus: false,
      initialData() {
        return [];
      },
    });
  };

  const getAllTaskTypes = () => {
    const taskTypeFilters = {
      companyId: currentUser?.companyId,
      status: Status.ACTIVE,
      sortBy: JSON.stringify([["name", "ASC"]]),
    };

    return useQuery({
      queryFn: async () => TasksTypesService.getAll(taskTypeFilters).then(({ data }) => data),
      queryKey: TaskTypeKeys.getAll([...Object.values(taskTypeFilters)]),
      enabled: !!currentUser?.id && !!currentUser?.companyId,
      refetchOnWindowFocus: false,
      initialData() {
        return [];
      },
    });
  };

  const getFleetTypes = () => {
    return useQuery({
      queryKey: FleetTypeKeys.getAll([]),
      queryFn: async () => {
        const response = await FleetTypeService.getAll({
          sortBy: JSON.stringify([["name", "ASC"]]),
        });

        if (response && response?.data?.length > 0) return response.data;

        return [];
      },
      enabled: !!currentUser?.id,
      staleTime: STALE_TIME,
      cacheTime: CACHE_TIME,
    });
  };

  const getAllSubcontractors = () => {
    const filters = {
      isActive: 1 as unknown as boolean,
      companyId: currentUser?.companyId!,
      sortBy: JSON.stringify([["businessName", "ASC"]]),
    } as Partial<Subcontractor>;

    return useQuery({
      queryKey: SubcontractorKeys.getAll([...Object.values(filters)]),
      queryFn: async () => {
        const response = await SubcontractorService.getAll(filters as any);

        if (response?.data) return response.data;

        return [];
      },
      enabled: !!currentUser?.companyId,
      staleTime: STALE_TIME,
      cacheTime: CACHE_TIME,
    });
  };

  const getAllMembers = (
    page?: number,
    pageSize?: number,
    searches?: Searches,
    filters?: Filters
  ) => {
    const includes = [
      {
        association: "userCompany",
        include: [
          {
            association: "role",
            attributes: ["id", "name"],
            required: !!getRelatedFilters(filters, "role"),
            where: Object.values(filters ?? {}).length ? getRelatedFilters(filters, "role") : null,
          },
        ],
        where: Object.values(filters ?? {}).length
          ? getRelatedFilters(filters, "userCompany")
          : null,
        required: !!(
          getRelatedFilters(filters, "role") || getRelatedFilters(filters, "userCompany")
        ),
      },
      {
        association: "location",
        attributes: ["id", "country", "longAddress"],
      },
    ];

    return useQuery({
      queryKey: MemberKeys.getAll(["equipments", page, pageSize, filters, searches, includes]),
      queryFn: async () => {
        const response = await MembersService.getAll({
          page,
          pageSize,
          companyId: currentUser?.companyId,
          subcontractorId: null,
          sortBy: JSON.stringify([["createdAt", "desc"]]),
          searchByOr: searches && searches.length > 0 ? JSON.stringify(searches) : "",
          include: JSON.stringify(includes),
          status: Status.ACTIVE,
          ...getRelatedFilters(filters, "member"),
        });

        if (response && response.data) return response.data;

        return [];
      },
      enabled: !!(currentUser?.id && currentUser?.companyId),
      refetchOnWindowFocus: false,
    });
  };

  const countMembers = (filters?: Filters, searches?: Searches) => {
    const includes = [
      {
        association: "userCompany",
        include: [
          {
            association: "role",
            attributes: ["id", "name"],
            required: !!getRelatedFilters(filters, "role"),
            where: Object.values(filters ?? {}).length ? getRelatedFilters(filters, "role") : null,
          },
        ],
        where: Object.values(filters ?? {}).length
          ? getRelatedFilters(filters, "userCompany")
          : null,
        required: !!(
          getRelatedFilters(filters, "role") || getRelatedFilters(filters, "userCompany")
        ),
      },
    ];

    return useQuery({
      queryKey: MemberKeys.count([filters, searches]),
      queryFn: async () => {
        const response = await MembersService.count({
          companyId: currentUser?.companyId,
          searchByOr: searches && searches.length > 0 ? JSON.stringify(searches) : "",
          include: JSON.stringify(includes),
          status: Status.ACTIVE,
          ...getRelatedFilters(filters, "member"),
        });

        if (response && response.data) return response.data;

        return 0;
      },
      enabled: !!(currentUser?.id && currentUser?.companyId),
      refetchOnWindowFocus: false,
    });
  };

  const getAllTeams = () => {
    const filters = {
      isActive: 1,
      companyId: currentUser?.companyId,
    };

    return useQuery({
      queryKey: TeamKeys.getAll([...Object.values(filters)]),
      queryFn: async () => {
        const response = await TeamService.getAll(filters as any);

        if (response?.data) return response.data;

        return [];
      },
      enabled: !!currentUser?.companyId,
      staleTime: STALE_TIME,
      cacheTime: CACHE_TIME,
    });
  };

  const getAllTeamsForEquipment = (params: GetTeamsFiltersParams) => {
    const { page = 0, pageSize = 10, sorting, searches, filters } = params;
    const internalFilters = {
      page,
      pageSize,
      searchByOr: searches?.length ? JSON.stringify(searches) : "",
      sortBy: sorting?.length ? JSON.stringify(sorting) : undefined,
      companyId: currentUser?.companyId,
      isCrew: false,
      ...filters,
    };

    return useQuery({
      queryKey: TeamKeys.getAll([...Object.values(internalFilters)]),
      queryFn: async () => {
        const response = await TeamService.getAll({
          ...internalFilters,
          include: JSON.stringify([
            {
              association: "fleets",
            },
            {
              association: "memberLeader",
            },
            {
              association: "members",
            },
          ]),
        });

        if (response?.data && Array.isArray(response.data)) return response.data;

        return [];
      },
      enabled: !!currentUser?.companyId,
      initialData() {
        return [];
      },
      refetchOnWindowFocus: false,
    });
  };

  const countTeams = (filters?: Filters, searches?: Searches) => {
    return useQuery({
      queryKey: TeamKeys.count([filters, searches]),
      queryFn: async () => {
        const response = await TeamService.count({
          companyId: currentUser?.companyId,
          searchByOr: searches && searches.length > 0 ? JSON.stringify(searches) : "",
          ...filters,
        });

        if (response && response.data) return response.data;

        return 0;
      },
      enabled: !!(currentUser?.id && currentUser?.companyId),
      refetchOnWindowFocus: false,
    });
  };

  const onBackToEquipmentsTab = () => {
    history?.push({
      pathname: PRIVATE_ROUTES.SETTINGS,
      state: {
        tab: translate("resources"),
        tabResource: translate("equipments"),
      },
    });
  };

  const getEquipmentById = (id: Fleet["id"] | undefined) => {
    const includes = [
      { association: "branches" },
      { association: "warehouse" },
      { association: "fleetType" },
      { association: "subContractor" },
      { association: "picture" },
      {
        association: "members",
        include: [
          {
            association: "userCompany",
            include: [{ association: "role", attributes: ["id", "name"] }],
          },
          {
            association: "location",
            attributes: ["id", "country", "longAddress"],
          },
        ],
      },
      {
        required: false,
        association: "teams",
        where: {
          isCrew: false,
        },
        include: [
          {
            association: "fleets",
          },
          {
            association: "memberLeader",
          },
          {
            association: "members",
          },
        ],
      },
    ];

    return useQuery({
      queryKey: FleetKeys.getById(id),
      queryFn: async () => {
        if (!id) return null;

        const response = await FleetService.getById(id, {
          include: JSON.stringify(includes),
        });

        if (response && response.data) return response.data;

        return null;
      },
      enabled: !!id && !!currentUser?.companyId,
      refetchOnWindowFocus: false,
    });
  };

  const useSaveEquipment = (callback: () => void) => {
    return useMutation({
      mutationKey: FleetKeys.update(),
      mutationFn: (values: UpdateFleet) => FleetService.update(values.id, values.fleet),
      onSuccess(_, variables) {
        queryClient.invalidateQueries({ queryKey: [FleetKeys.getAll] });
        queryClient.invalidateQueries({ queryKey: [FleetKeys.all, variables.id] });
        NotificationToast.success(translate("operationPerformedSuccessfully"));
        callback();
      },
    });
  };

  const getFleetTypeModes = useCallback(() => {
    return [
      { label: FleetTypeMode.PANTECH, value: FleetTypeMode.PANTECH },
      { label: FleetTypeMode.FLATBED, value: FleetTypeMode.FLATBED },
    ];
  }, []);

  const getOwnerhips = useCallback(() => {
    return [
      { label: translate("own"), value: EquipmentOwnership.OWN },
      { label: translate("subcontractor"), value: EquipmentOwnership.SUBCONTRACTOR },
      { label: translate("rental"), value: EquipmentOwnership.RENTAL },
      { label: translate("leased"), value: EquipmentOwnership.LEASED },
    ];
  }, []);

  const getFleetTeams = (params: GetTeamsFiltersParams) => {
    const { page = 0, pageSize = 10, equipmentId, sorting, searches, filters } = params;
    const internalFilters = {
      isActive: 1,
      page,
      pageSize,
      searchByOr: searches?.length ? JSON.stringify(searches) : "",
      sortBy: sorting?.length ? JSON.stringify(sorting) : undefined,
      companyId: currentUser?.companyId,
      ...filters,
    };

    return useQuery({
      queryKey: TeamKeys.getAll([...Object.values(internalFilters), equipmentId]),
      queryFn: async () => {
        const response = await TeamService.getAll({
          ...internalFilters,
          include: JSON.stringify([
            {
              association: "fleets",
              required: true,
              where: {
                id: equipmentId,
              },
            },
            {
              association: "memberLeader",
            },
            {
              association: "members",
            },
          ]),
        });

        if (response?.data && Array.isArray(response.data)) return response.data;

        return [];
      },
      enabled: !!currentUser?.companyId && !!equipmentId,
      // staleTime: STALE_TIME,
      // cacheTime: CACHE_TIME,
      initialData() {
        return [];
      },
      refetchOnWindowFocus: false,
    });
  };

  const getFleetMembersWithCount = (params: GetTeamsFiltersParams) => {
    const { page = 0, pageSize = 10, equipmentId, sorting, searches, filters } = params;
    const internalFilters = {
      page,
      pageSize,
      searchByOr: searches?.length ? JSON.stringify(searches) : "",
      sortBy: sorting?.length ? JSON.stringify(sorting) : undefined,
      companyId: currentUser?.companyId,
    };

    return useQuery({
      queryKey: MemberKeys.getAndCountAll([
        ...Object.values(internalFilters),
        filters,
        equipmentId,
      ]),
      queryFn: async () => {
        const response = await MembersService.getAndCountAll({
          ...internalFilters,

          include: JSON.stringify([
            {
              association: "userCompany",
              include: [
                {
                  association: "role",
                  attributes: ["id", "name"],
                  required: !!getRelatedFilters(filters, "role"),
                  where: Object.values(filters ?? {}).length
                    ? getRelatedFilters(filters, "role")
                    : null,
                },
                {
                  association: "applications",
                  attributes: ["id", "name"],
                  required: !!getRelatedFilters(filters, "application"),
                  where: Object.values(filters ?? {}).length
                    ? getRelatedFilters(filters, "application")
                    : null,
                },
              ],
              required: !!(
                getRelatedFilters(filters, "role") || getRelatedFilters(filters, "application")
              ),
            },
            {
              association: "location",
              attributes: ["id", "longAddress"],
            },
            {
              association: "fleets",
              required: true,
              where: { id: equipmentId },
            },
          ]),
          ...getRelatedFilters(filters, "member"),
        });

        if (response?.data) return response.data;

        return {
          rows: [],
          count: 0,
        };
      },
      enabled: !!currentUser?.companyId && !!equipmentId,
      // staleTime: STALE_TIME,
      // cacheTime: CACHE_TIME,
      initialData() {
        return {
          rows: [],
          count: 0,
        };
      },
      refetchOnWindowFocus: false,
    });
  };

  const getFleetMembersCount = (params: GetTeamsCountParams) => {
    const { equipmentId, filters, searches, sorting } = params;
    const internalFilters = {
      companyId: currentUser?.companyId,
      searchByOr: searches?.length ? JSON.stringify(searches) : "",
      sortBy: sorting?.length ? JSON.stringify(sorting) : undefined,
    };

    return useQuery({
      queryKey: MemberKeys.count([
        ...Object.values(internalFilters),
        filters,
        equipmentId,
        sorting,
        searches,
      ]),
      queryFn: async () => {
        return MembersService.count({
          ...internalFilters,
          include: JSON.stringify([
            {
              association: "userCompany",
              include: [
                {
                  association: "role",
                  attributes: ["id", "name"],
                  required: !!getRelatedFilters(filters, "role"),
                  where: Object.values(filters ?? {}).length
                    ? getRelatedFilters(filters, "role")
                    : null,
                },
                {
                  association: "applications",
                  attributes: ["id", "name"],
                  required: !!getRelatedFilters(filters, "application"),
                  where: Object.values(filters ?? {}).length
                    ? getRelatedFilters(filters, "application")
                    : null,
                },
              ],
              required: !!(
                getRelatedFilters(filters, "role") || getRelatedFilters(filters, "application")
              ),
            },
            {
              association: "location",
              attributes: ["id", "longAddress"],
            },
            {
              association: "teams",
              required: true,
              include: [{ association: "fleets", required: true, id: equipmentId }],
            },
          ]),
          ...getRelatedFilters(filters, "member"),
          distinctCount: true,
        }).then(({ data }) => data);
      },
      enabled: !!currentUser?.companyId && !!equipmentId,
      // staleTime: STALE_TIME,
      // cacheTime: CACHE_TIME,
      refetchOnWindowFocus: false,
    });
  };

  const getApplications = () => {
    return useQuery({
      queryKey: ApplicationKeys.getAll([]),
      queryFn: async () => {
        const response = await ApplicationsService.getAll({
          include: JSON.stringify([
            {
              association: "profiles",
              where: {
                initials: currentUser?.company?.profile?.initials,
              },
            },
          ]),
        });

        if (response?.data && Array.isArray(response.data)) return response.data;

        return [];
      },
      enabled: !!currentUser?.companyId,
      refetchOnWindowFocus: false,
      initialData() {
        return [];
      },
    });
  };

  const getRoles = () => {
    return useQuery({
      queryKey: RoleKeys.getAll([]),
      queryFn: async () => {
        const response = await RoleService.getAll({
          companyId: currentUser?.companyId,
          status: Status.ACTIVE,
        });

        if (response && response?.data) return response.data;

        return [];
      },
      enabled: !!currentUser?.companyId,
      refetchOnWindowFocus: false,
      initialData() {
        return [];
      },
    });
  };

  const getFleetTeamsCount = (params: GetTeamsCountParams) => {
    const { equipmentId, filters, searches, sorting } = params;
    const internalFilters = {
      isActive: 1,
      companyId: currentUser?.companyId,
      searchByOr: searches?.length ? JSON.stringify(searches) : "",
      sortBy: sorting?.length ? JSON.stringify(sorting) : undefined,
      ...filters,
    };

    return useQuery({
      queryKey: TeamKeys.count([...Object.values(internalFilters), equipmentId, sorting, searches]),
      queryFn: async () => {
        return TeamService.count({
          ...internalFilters,
          include: JSON.stringify([
            {
              association: "fleets",
              required: true,
              where: {
                id: equipmentId,
              },
              attributes: ["id"],
            },
          ]),
        }).then(({ data }) => data);
      },
      enabled: !!currentUser?.companyId && !!equipmentId,
      // staleTime: STALE_TIME,
      // cacheTime: CACHE_TIME,
      refetchOnWindowFocus: false,
    });
  };

  const getFleetAvabilities = (params: AvailabilityFiltersParams) => {
    const { page = 0, pageSize = 10, equipmentId, filters, searches } = params;

    return useQuery({
      queryKey: AvailabilityKeys.getAll([page, pageSize, equipmentId, filters, searches]),
      queryFn: async () => {
        const response = await AvailabilityService.getAll({
          page,
          pageSize,
          companyId: currentUser?.companyId,
          searchByOr: searches?.length ? JSON.stringify(searches) : "",
          include: JSON.stringify([
            {
              association: "equipments",
              required: true,
              where: {
                id: equipmentId,
              },
              attributes: ["id"],
            },
            {
              association: "createdBy",
              attributes: ["id", "fullName"],
            },
          ]),
          ...getRelatedFilters(filters, "availability"),
        });

        if (response?.data && Array.isArray(response.data)) return response.data;

        return [];
      },
      initialData() {
        return [];
      },
      enabled: !!currentUser?.companyId && !!equipmentId,
      refetchOnWindowFocus: false,
    });
  };

  const getFleetAvabilitiesCount = (params: AvailabilityCountParams) => {
    const { equipmentId, filters, searches } = params;

    return useQuery({
      queryKey: AvailabilityKeys.count([equipmentId, filters, searches]),
      queryFn: async () => {
        const response = await AvailabilityService.count({
          companyId: currentUser?.companyId,
          include: JSON.stringify([
            {
              association: "equipments",
              where: {
                id: equipmentId,
              },
              attributes: ["id"],
            },
          ]),
          distinctCount: true,
          ...getRelatedFilters(filters, "availability"),
        });

        if (response?.data) return response.data;

        return 0;
      },
      enabled: !!currentUser?.companyId && !!equipmentId,
      refetchOnWindowFocus: false,
    });
  };

  const getFleetOrdersWithCount = (params: GetTeamsFiltersParams) => {
    const { page = 0, pageSize = 10, equipmentId, filters, searches, sorting } = params;

    const internalFilters = {
      page,
      pageSize,
      searchByOr: searches?.length ? JSON.stringify(searches) : "",
      sortBy: sorting?.length ? JSON.stringify(sorting) : undefined,
      companyId: currentUser?.companyId,
    };

    return useQuery(
      [
        "orders.by.equipment.listAndCountAll",
        ...Object.values(internalFilters),
        page,
        pageSize,
        sorting,
        searches,
      ],
      async () => {
        const response = await JobService.getAndCountAll({
          attributes: [
            "id",
            "reference",
            "type",
            "mode",
            "volume",
            "updatedAt",
            "createdAt",
            "customVolumeActual",
            "status",
            "leadSourceFrom",
          ],
          companyId: currentUser?.companyId,
          page,
          pageSize,
          sortBy:
            sorting && sorting?.length > 0
              ? JSON.stringify(sorting)
              : JSON.stringify([["updatedAt", "DESC"]]),
          include: JSON.stringify([
            {
              required: true,
              association: "tasks",
              attributes: [],
              include: [
                {
                  required: true,
                  association: "fleets",
                  attributes: [],
                  where: {
                    id: equipmentId,
                  },
                },
              ],
            },
            {
              association: "origins",
              required: false,
              attributes: ["id", "longAddress", "createdAt"],
              order: ["createdAt", "desc"],
              through: {
                attributes: [],
              },
            },
            {
              required: false,
              association: "destinations",
              attributes: ["id", "longAddress", "createdAt"],
              order: ["createdAt", "desc"],
              through: {
                attributes: [],
              },
            },
            {
              association: "customer",
              required: false,
              attributes: ["id", "fullName"],
            },
            {
              association: "itemsJob",
              attributes: ["id", "quantity", "volume", "status", "subStatus"],
              include: [
                { association: "destination" },
                {
                  association: "mapCells",
                  attributes: ["id", "name", "mapId"],
                  include: [
                    {
                      association: "map",
                      attributes: ["id", "warehouseId", "sectionId", "name"],
                      include: [
                        { association: "warehouse", attributes: ["id", "name"] },
                        { association: "section", attributes: ["id", "name"] },
                      ],
                    },
                  ],
                },
              ],
            },
            {
              association: "storageBoxes",
              attributes: ["id", "number", "storageBoxTypeId"],
              include: [
                {
                  association: "storageBoxType",
                  attributes: ["id", "name"],
                },
              ],
            },
            {
              association: "materials",
              attributes: ["id", "volume", "quantity"],
              include: [
                {
                  association: "serviceBooking",
                  attributes: ["id", "isItem"],
                },
                {
                  association: "destination",
                  attributes: ["id", "name"],
                },
              ],
            },
          ]),
          searchByOr: searches && searches?.length > 0 ? JSON.stringify(searches) : "",
          ...filters,
        });

        if (response?.data) return response?.data;

        return { rows: [], count: 0 };
      },
      {
        enabled: !!currentUser?.id && !!equipmentId,
        refetchOnWindowFocus: false,
        // staleTime: STALE_TIME,
        // cacheTime: CACHE_TIME,
        initialData() {
          return { rows: [], count: 0 };
        },
      }
    );
  };

  const getJobTypes = () => {
    return useQuery(
      ["jobType.list", currentUser?.companyId],
      async () => {
        const response = await JobTypeService.getAll({
          companyId: currentUser?.companyId,
          status: Status.ACTIVE,
          sortBy: JSON.stringify([["name", "asc"]]),
        });

        if (response?.data) return response.data;

        return [];
      },
      {
        refetchOnWindowFocus: false,
        enabled: !!currentUser?.companyId,
        initialData() {
          return [];
        },
      }
    );
  };

  const getJobStatuses = () => {
    return useQuery(
      ["statuses.list.order", currentUser?.companyId],
      async () => {
        const response = await StatusesService.getAll({
          status: Status.ACTIVE,
          companyId: currentUser?.companyId,
          usedBy: SubStatusUsedBy.ORDERS,
        });

        if (response?.data) return response.data;

        return [];
      },
      {
        enabled: !!currentUser?.companyId,
        refetchOnWindowFocus: false,
        initialData() {
          return [];
        },
      }
    );
  };

  return {
    getAllFleet,
    countFleet,
    useCreateFleet,
    useUpdateFleet,
    getAllBranches,
    getAllWarehouses,
    getFleetTypes,
    getAllSubcontractors,
    getAllMembers,
    countMembers,
    getAllTeams,
    countTeams,
    getAllTeamsForEquipment,
    onBackToEquipmentsTab,
    getEquipmentById,
    useSaveEquipment,
    getFleetTypeModes,
    getOwnerhips,
    getAllTaskTypes,
    getFleetAvabilities,
    getFleetAvabilitiesCount,
    getFleetTeams,
    getFleetTeamsCount,
    getFleetMembersWithCount,
    getFleetMembersCount,
    getApplications,
    getRoles,
    getFleetOrdersWithCount,
    getJobStatuses,
    getJobTypes,
  };
};

export default useEquipmentActions;
